/**
 * SprayWeatherWidget.tsx
 *
 * 7-dages sprøjtevejr-widget til Leaflet-kort.
 * Modtager fieldCenters prop — beregner centroid og henter regional vejrdata.
 *
 * Betingelser for godt sprøjtevejr:
 *  - Vind < 4 m/s
 *  - Temperatur 8–18 °C
 *  - Ingen regn
 *
 * Data hentes lazy: kun når bruger er online, max 4x/dag per grid-celle.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import './SprayWeatherWidget.css';

// ─── Typer ───────────────────────────────────────────────
interface WeatherPoint {
  time: string;
  hour: number;
  day: string;
  dayNum: number;
  wind: number;
  rain: number;
  temp: number;
  isGood: boolean;
}

interface SprayWindow {
  start: number;
  end: number;
  hours: number;
}

interface DaySection {
  start: number;
  end: number;
  day: string;
  dayNum: number;
}

interface ForecastResponse {
  data: WeatherPoint[];
  generatedAt: string;
  nextUpdate: string;
  source: string;
}

interface SprayWeatherWidgetProps {
  /** Centrum-koordinater for landmandens marker. Widget beregner centroid. */
  fieldCenters?: { lat: number; lon: number }[];
}

// ─── Konstanter ──────────────────────────────────────────
const SVG_NS = 'http://www.w3.org/2000/svg';
const PAD_L = 22;
const PAD_R = 20;
const W = 390;
const CW = W - PAD_L - PAD_R;
const H_WIND = 32;
const H_RAIN = 16;
const H_TOTAL = H_WIND + H_RAIN + 10;
const MAX_WIND = 8;
const MAX_RAIN = 3;
const MAX_TEMP = 16;
const DEFAULT_LAT = 56.0;
const DEFAULT_LON = 9.5;
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4002';

// ─── Hjælpefunktioner ────────────────────────────────────
function xOf(i: number, n: number): number {
  return PAD_L + (i / (n - 1)) * CW;
}
function yWind(v: number): number {
  return H_WIND - (v / MAX_WIND) * (H_WIND - 4);
}
function yTemp(v: number): number {
  return H_WIND - (v / MAX_TEMP) * (H_WIND - 4);
}
function svgEl(tag: string, attrs?: Record<string, string>): SVGElement {
  const el = document.createElementNS(SVG_NS, tag);
  if (attrs) Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
}
function smoothPath(points: { x: number; y: number }[]): string {
  if (points.length === 0) return '';
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length; i++) {
    const cx = (points[i - 1].x + points[i].x) / 2;
    const cy = (points[i - 1].y + points[i].y) / 2;
    d += ` Q ${points[i - 1].x} ${points[i - 1].y} ${cx} ${cy}`;
  }
  d += ` L ${points[points.length - 1].x} ${points[points.length - 1].y}`;
  return d;
}
function findGoodWindows(data: WeatherPoint[]): SprayWindow[] {
  const windows: SprayWindow[] = [];
  let start: number | null = null;
  for (let i = 0; i < data.length; i++) {
    if (data[i].isGood && start === null) start = i;
    if ((!data[i].isGood || i === data.length - 1) && start !== null) {
      const end = data[i].isGood ? i : i - 1;
      if (end - start >= 1) windows.push({ start, end, hours: (end - start + 1) * 3 });
      start = null;
    }
  }
  return windows;
}
function findDaySections(data: WeatherPoint[]): DaySection[] {
  const sections: DaySection[] = [];
  let secStart = 0;
  for (let i = 1; i < data.length; i++) {
    if (data[i].day !== data[i - 1].day) {
      sections.push({ start: secStart, end: i - 1, day: data[secStart].day, dayNum: data[secStart].dayNum });
      secStart = i;
    }
  }
  sections.push({ start: secStart, end: data.length - 1, day: data[secStart].day, dayNum: data[secStart].dayNum });
  return sections;
}

/** Beregn centroid af et array af koordinater */
function computeCentroid(points: { lat: number; lon: number }[]): { lat: number; lon: number } {
  if (points.length === 0) return { lat: DEFAULT_LAT, lon: DEFAULT_LON };
  const sum = points.reduce((acc, p) => ({ lat: acc.lat + p.lat, lon: acc.lon + p.lon }), { lat: 0, lon: 0 });
  return { lat: sum.lat / points.length, lon: sum.lon / points.length };
}

async function fetchForecast(lat: number, lon: number): Promise<WeatherPoint[]> {
  try {
    const res = await fetch(`${API_URL}/api/spray-weather/forecast?lat=${lat.toFixed(2)}&lon=${lon.toFixed(2)}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json: ForecastResponse = await res.json();
    return json.data;
  } catch (err) {
    console.error('[SprayWeatherWidget] Kan ikke hente forecast:', err);
    return [];
  }
}

// ─── Komponent ───────────────────────────────────────────
const SprayWeatherWidget: React.FC<SprayWeatherWidgetProps> = ({ fieldCenters }) => {
  const [expanded, setExpanded] = useState(true);
  const [data, setData] = useState<WeatherPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const dayLabelsRef = useRef<HTMLDivElement>(null);

  // Beregn centroid fra marker-koordinater (memoized)
  const centroid = useMemo(() => {
    if (!fieldCenters || fieldCenters.length === 0) return { lat: DEFAULT_LAT, lon: DEFAULT_LON };
    return computeCentroid(fieldCenters);
  }, [fieldCenters]);

  // Hent data baseret på centroid
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    fetchForecast(centroid.lat, centroid.lon).then(result => {
      if (!mounted) return;
      if (result.length > 0) { setData(result); setError(false); }
      else setError(true);
      setLoading(false);
    });
    return () => { mounted = false; };
  }, [centroid.lat, centroid.lon]);

  const renderChart = useCallback(() => {
    const svg = svgRef.current;
    const tooltip = tooltipRef.current;
    const dayLabelsEl = dayLabelsRef.current;
    if (!svg || !tooltip || !dayLabelsEl || data.length === 0) return;

    svg.innerHTML = '';
    dayLabelsEl.innerHTML = '';
    const N = data.length;
    const goodWindows = findGoodWindows(data);
    const daySections = findDaySections(data);

    // Gradient
    const defs = svgEl('defs');
    const lg = svgEl('linearGradient', { id: 'sw-wg', x1: '0', y1: '0', x2: '0', y2: '1' });
    lg.append(
      svgEl('stop', { offset: '0%', 'stop-color': '#3b82f6', 'stop-opacity': '0.2' }),
      svgEl('stop', { offset: '100%', 'stop-color': '#3b82f6', 'stop-opacity': '0.02' })
    );
    defs.append(lg);
    svg.append(defs);

    // Grønne sprøjtezoner
    goodWindows.forEach(w => {
      svg.append(svgEl('rect', {
        x: String(xOf(w.start, N)), y: '0',
        width: String(xOf(w.end, N) - xOf(w.start, N)), height: String(H_TOTAL),
        fill: '#16a34a', opacity: '0.12', rx: '2'
      }));
    });

    // Dag-separatorer (sort solid)
    for (let i = 1; i < N; i++) {
      if (data[i].day !== data[i - 1].day) {
        svg.append(svgEl('line', {
          x1: String(xOf(i, N)), y1: '0', x2: String(xOf(i, N)), y2: String(H_TOTAL),
          stroke: '#000000', 'stroke-width': '1', opacity: '0.35'
        }));
      }
    }

    // Y-akse (m/s) + guidelinjer
    [0, 4, 8].forEach(v => {
      const y = yWind(v);
      const label = svgEl('text', {
        x: String(PAD_L - 3), y: String(y + 3),
        'font-size': '8', fill: '#9ca3af',
        'text-anchor': 'end', 'font-family': 'system-ui, sans-serif'
      });
      label.textContent = String(v);
      svg.append(label);
      if (v > 0) {
        svg.append(svgEl('line', {
          x1: String(PAD_L), y1: String(y), x2: String(W), y2: String(y),
          stroke: v === 4 ? '#ef4444' : '#e5e7eb', 'stroke-width': '0.5',
          'stroke-dasharray': v === 4 ? '3,2' : '2,3',
          opacity: v === 4 ? '0.4' : '0.3'
        }));
      }
    });

    // Vindkurve (blå)
    const windPts = data.map((d, i) => ({ x: xOf(i, N), y: yWind(d.wind) }));
    const windPath = smoothPath(windPts);
    svg.append(svgEl('path', { d: windPath + ` L ${PAD_L + CW} ${H_WIND} L ${PAD_L} ${H_WIND} Z`, fill: 'url(#sw-wg)' }));
    svg.append(svgEl('path', { d: windPath, fill: 'none', stroke: '#3b82f6', 'stroke-width': '1.5', 'stroke-linecap': 'round' }));

    // Temperaturkurve (orange stiplet)
    const tempPts = data.map((d, i) => ({ x: xOf(i, N), y: yTemp(d.temp) }));
    const tempPath = smoothPath(tempPts);
    svg.append(svgEl('path', {
      d: tempPath, fill: 'none', stroke: '#e57a1a', 'stroke-width': '1.2',
      'stroke-dasharray': '4,2', 'stroke-linecap': 'round', opacity: '0.7'
    }));

    // Højre y-akse (°C)
    [0, 5, 10, 15].forEach(v => {
      const y = yTemp(v);
      const label = svgEl('text', {
        x: String(PAD_L + CW + 3), y: String(y + 3),
        'font-size': '7', fill: '#e57a1a', 'text-anchor': 'start',
        'font-family': 'system-ui, sans-serif', opacity: '0.6'
      });
      label.textContent = v + '\u00b0';
      svg.append(label);
    });

    // Regnbjælker
    data.forEach((d, i) => {
      if (d.rain === 0) return;
      const barH = (d.rain / MAX_RAIN) * H_RAIN;
      svg.append(svgEl('rect', {
        x: String(xOf(i, N) - 2), y: String(H_WIND + 2 + (H_RAIN - barH)),
        width: '4', height: String(barH), fill: '#60a5fa', opacity: '0.6', rx: '1'
      }));
    });

    // Hover-elementer
    const hoverLine = svgEl('line', { y1: '0', y2: String(H_TOTAL), stroke: '#374151', 'stroke-width': '1', opacity: '0' });
    const hoverDot = svgEl('circle', { r: '3', fill: '#3b82f6', stroke: 'white', 'stroke-width': '1.5', opacity: '0' });
    const hoverDotTemp = svgEl('circle', { r: '2.5', fill: '#e57a1a', stroke: 'white', 'stroke-width': '1.5', opacity: '0' });
    svg.append(hoverLine, hoverDot, hoverDotTemp);

    const sliceW = W / N;
    data.forEach((d, i) => {
      const hitbox = svgEl('rect', {
        x: String(xOf(i, N) - sliceW / 2), y: '0',
        width: String(sliceW), height: String(H_TOTAL),
        fill: 'transparent', style: 'cursor:crosshair'
      });
      hitbox.addEventListener('mouseenter', () => {
        const x = xOf(i, N);
        hoverLine.setAttribute('x1', String(x)); hoverLine.setAttribute('x2', String(x)); hoverLine.setAttribute('opacity', '0.25');
        hoverDot.setAttribute('cx', String(x)); hoverDot.setAttribute('cy', String(yWind(d.wind))); hoverDot.setAttribute('opacity', '1');
        hoverDotTemp.setAttribute('cx', String(x)); hoverDotTemp.setAttribute('cy', String(yTemp(d.temp))); hoverDotTemp.setAttribute('opacity', '1');
        const good = d.isGood ? ' <span class="sw-tooltip-good">\u2713</span>' : '';
        tooltip.innerHTML = `<strong>${d.day} ${d.dayNum}. ${String(d.hour).padStart(2, '0')}:00</strong> \u2014 ${d.wind.toFixed(1)} m/s \u00b7 ${d.rain.toFixed(1)} mm \u00b7 ${d.temp}\u00b0C${good}`;
        tooltip.style.display = 'block';
      });
      hitbox.addEventListener('mousemove', (e: MouseEvent) => {
        const tw = tooltip.offsetWidth || 180;
        const spaceRight = window.innerWidth - e.clientX;
        if (spaceRight < tw + 20) {
          tooltip.style.left = (e.clientX - tw - 14) + 'px';
        } else {
          tooltip.style.left = (e.clientX + 14) + 'px';
        }
        tooltip.style.top = (e.clientY - 40) + 'px';
      });
      svg.append(hitbox);
    });

    svg.addEventListener('mouseleave', () => {
      tooltip.style.display = 'none';
      hoverLine.setAttribute('opacity', '0');
      hoverDot.setAttribute('opacity', '0');
      hoverDotTemp.setAttribute('opacity', '0');
    });

    // Dag-labels (centreret)
    daySections.forEach(sec => {
      const centerX = (xOf(sec.start, N) + xOf(sec.end, N)) / 2;
      const lbl = document.createElement('div');
      lbl.className = 'sw-day-label';
      lbl.style.left = centerX + 'px';
      lbl.style.transform = 'translateX(-50%)';
      lbl.textContent = sec.day + ' ' + sec.dayNum + '.';
      dayLabelsEl.append(lbl);
    });
  }, [data]);

  useEffect(() => { if (data.length > 0 && expanded) renderChart(); }, [data, expanded, renderChart]);

  const collapsedSummary = useMemo(() => {
    if (data.length === 0) return { text: 'Indlæser...', color: '#6b7280' };
    const windows = findGoodWindows(data);
    if (windows.length === 0) return { text: 'Ingen gode vinduer', color: '#dc2626' };
    let best = windows[0];
    windows.forEach(w => { if (w.hours > best.hours) best = w; });
    const d = data[best.start];
    return { text: `${d.day} ${d.dayNum}. \u2014 ${best.hours}t godt vejr`, color: '#16a34a' };
  }, [data]);

  const toggleWidget = () => setExpanded(prev => !prev);

  if (loading) {
    return (
      <div className="sw-widget">
        <div className="sw-header">
          <div className="sw-header-left">
            <span className="sw-title">{'\u26c5'} Sprøjtevejr 7d</span>
          </div>
        </div>
        <div className="sw-loading">Henter vejrdata...</div>
      </div>
    );
  }

  if (error || data.length === 0) {
    return (
      <div className="sw-widget sw-collapsed" onClick={toggleWidget}>
        <div className="sw-collapsed-inner">
          <span style={{ fontSize: 14 }}>{'\u26c5'}</span>
          <div>
            <div className="sw-collapsed-title">Sprøjtevejr</div>
            <div className="sw-collapsed-sub" style={{ color: '#dc2626' }}>Vejrdata utilgængelig</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={`sw-widget ${expanded ? '' : 'sw-collapsed'}`}>
        {expanded && (
          <div className="sw-expanded">
            <div className="sw-header">
              <div className="sw-header-left">
                <span className="sw-title">{'\u26c5'} Sprøjtevejr 7d</span>
                <div className="sw-legend">
                  <span className="sw-legend-item"><span className="sw-legend-wind" />vind</span>
                  <span className="sw-legend-item"><span className="sw-legend-temp" />{'\u00b0'}C</span>
                  <span className="sw-legend-item"><span className="sw-legend-rain" />regn</span>
                  <span className="sw-legend-item"><span className="sw-legend-good" />godt</span>
                </div>
              </div>
              <button className="sw-close" onClick={toggleWidget} title="Minimer">{'\u2715'}</button>
            </div>
            <div className="sw-chart-area">
              <svg ref={svgRef} width={W} height={H_TOTAL} style={{ display: 'block' }} />
            </div>
            <div className="sw-day-labels">
              <div className="sw-day-labels-inner" ref={dayLabelsRef} />
            </div>
          </div>
        )}
        {!expanded && (
          <div className="sw-collapsed-view" onClick={toggleWidget}>
            <div className="sw-collapsed-inner">
              <span style={{ fontSize: 14 }}>{'\u26c5'}</span>
              <div>
                <div className="sw-collapsed-title">Sprøjtevejr</div>
                <div className="sw-collapsed-sub" style={{ color: collapsedSummary.color }}>{collapsedSummary.text}</div>
              </div>
              <span className="sw-collapsed-arrow">{'\u25bc'}</span>
            </div>
          </div>
        )}
      </div>
      <div className="sw-tooltip" ref={tooltipRef} />
    </>
  );
};

export default SprayWeatherWidget;
